import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaEnvelopeOpenText,
  FaShieldAlt,
} from "react-icons/fa";

import {
  getCurrentUser,
  setSession,
} from "../goourhub/component/session";

import "./AccountGate.css";
import logo from "../logo.png";
const API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP_BY_STAGE = {
  email: 1,
  code: 2,
  verified: 3,
};

function getApiErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;

  return (
    data?.message ||
    data?.body?.message ||
    (typeof data === "string" ? data : "") ||
    error?.message ||
    fallbackMessage
  );
}

function capitalizeFirstName(value) {
  const clean = String(value || "").trim();

  if (!clean) {
    return "";
  }

  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function accountFromSession(session) {
  if (!session?.email || !session?.token) {
    return null;
  }

  return {
  
  email: String(session.email).trim().toLowerCase(),

  // This is the reusable signed GoOurHub session JWT.
  sessionToken: session.token,

  // A restored session is NOT an OTP verification challenge.
  verificationToken: "",

  accountExists: true,
    firstName: capitalizeFirstName(session.firstName),
    lastName: String(session.lastName || "").trim(),
    restoredFromSession: true,
  };
}

export default function AccountGate({ children }) {
  /*
   * IMPORTANT:
   * Read the same session used by Header/Profile/Favorites BEFORE
   * deciding which screen to show.
   *
   * If email + token already exist, the visitor is already signed
   * in and we go directly to PartnerSignUp ("business").
   */
  const savedSession = getCurrentUser();
  const restoredAccount = accountFromSession(savedSession);

  const [stage, setStage] = useState(
    restoredAccount ? "business" : "email"
  );

  const [email, setEmail] = useState(
    savedSession?.email
      ? String(savedSession.email).trim().toLowerCase()
      : ""
  );

  const [challengeId, setChallengeId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const [verifiedAccount, setVerifiedAccount] = useState(
    restoredAccount
  );

  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  const normalizedEmail = email.trim().toLowerCase();

  const requestVerificationCode = async () => {
    setErrorMessage("");

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setErrorMessage("Enter a valid email address.");
      return;
    }

    setIsBusy(true);

    try {
      const response = await axios.post(
        `${API_BASE}/api/auth/email/start`,
        {
          email: normalizedEmail,
          purpose: "partner-signup",
        }
      );

      const returnedChallengeId =
        response?.data?.challengeId;

      if (!returnedChallengeId) {
        throw new Error(
          "The server did not return an email challenge ID."
        );
      }

      setChallengeId(returnedChallengeId);
      setVerificationCode("");
      setStage("code");
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "We could not send the verification code. Please try again."
        )
      );
    } finally {
      setIsBusy(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    await requestVerificationCode();
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const cleanCode = verificationCode.replace(
      /\D/g,
      ""
    );

    if (!challengeId || cleanCode.length !== 6) {
      setErrorMessage(
        "Enter the 6-digit verification code."
      );
      return;
    }

    setIsBusy(true);

    try {
      const response = await axios.post(
        `${API_BASE}/api/auth/email/verify`,
        {
          challengeId,
          code: cleanCode,
        }
      );

    const verificationToken =
  response?.data?.verificationToken;

const sessionToken =
  response?.data?.sessionToken;

if (!verificationToken) {
  throw new Error(
    "The server did not return a verification token."
  );
}

if (!sessionToken) {
  throw new Error(
    "The server did not return a session token."
  );
}

      const returnedAccount =
        response?.data?.account || {};

      const returnedFirstName = String(
        response?.data?.firstName ||
          response?.data?.first_name ||
          returnedAccount?.firstName ||
          returnedAccount?.first_name ||
          savedSession?.firstName ||
          ""
      ).trim();

      const returnedLastName = String(
        response?.data?.lastName ||
          response?.data?.last_name ||
          returnedAccount?.lastName ||
          returnedAccount?.last_name ||
          savedSession?.lastName ||
          ""
      ).trim();

     const nextVerifiedAccount = {
  email: normalizedEmail,
  verificationToken,
  sessionToken,
  accountExists: Boolean(
          response?.data?.accountExists
        ),
        firstName: capitalizeFirstName(
          returnedFirstName
        ),
        lastName: returnedLastName,
        restoredFromSession: false,
      };

      setVerifiedAccount(nextVerifiedAccount);

      /*
       * The same session powers the Header, Profile, Favorites,
       * and now Add Business.
       *
       * session.js currently preserves email, firstName and token.
       * If you later add lastName to session.js, this object is
       * already ready to store it too.
       */
 setSession({
  email: normalizedEmail,
  firstName: returnedFirstName
    ? returnedFirstName.charAt(0).toUpperCase() +
      returnedFirstName.slice(1)
    : "",
  token: sessionToken,
});

      setStage("verified");
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "The verification code could not be verified."
        )
      );
    } finally {
      setIsBusy(false);
    }
  };

  const resetAccountGate = () => {
    setStage("email");

    /*
     * If there is already a site session, keep its email filled.
     * The person should never have to type the same signed-in
     * email again just because they reset this screen.
     */
    setEmail(
      savedSession?.email
        ? String(savedSession.email)
            .trim()
            .toLowerCase()
        : ""
    );

    setChallengeId("");
    setVerificationCode("");
    setVerifiedAccount(null);
    setErrorMessage("");
  };

  /*
   * Header back button:
   *  - code screen     -> email screen
   *  - verified screen -> start over
   *  - email screen    -> previous page or home
   */
  const handleBack = () => {
    setErrorMessage("");

    if (stage === "code") {
      setVerificationCode("");
      setStage("email");
      return;
    }

    if (stage === "verified") {
      resetAccountGate();
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  /*
   * This is the important path for your Header -> Add Business flow.
   *
   * If AccountGate found an existing saved session with email + token,
   * stage started as "business", so the email/OTP gate is never shown.
   *
   * PartnerSignUp receives the restored account and can immediately:
   *   - show "Signed in as ..."
   *   - prefill/lock email
   *   - prefill first name
   *   - treat the current verification token as verified
   */
  if (stage === "business") {
    if (React.isValidElement(children)) {
      return React.cloneElement(children, {
        verifiedAccount,
      });
    }

    return <>{children}</>;
  }

  const currentStep =
    STEP_BY_STAGE[stage] || 1;

  const isReturningUser = Boolean(
    verifiedAccount?.accountExists
  );

  const firstName =
    verifiedAccount?.firstName || "";

  let greeting = "Email verified 👋";

  if (isReturningUser && firstName) {
    greeting = `Hi ${firstName}, welcome back 👋`;
  } else if (isReturningUser) {
    greeting = "Welcome back 👋";
  }

  const sessionHasKnownEmail = Boolean(
    savedSession?.email &&
      normalizedEmail ===
        String(savedSession.email)
          .trim()
          .toLowerCase()
  );

  return (
    <div className="account-gate-shell">
      {/* ---------- Top bar ---------- */}
      <header className="account-gate-header">
        <button
          type="button"
          className="account-gate-back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <FaArrowLeft aria-hidden="true" />
        </button>

     <Link to="/" className="account-gate-brand">
  <img
    src={logo}
    alt="GoOurHub logo"
    className="account-gate-brand-logo"
  />
  GoOurHub
</Link>

        <span
          className="account-gate-step"
          aria-label={`Step ${currentStep} of 3`}
        >
          {currentStep}
          <em>/3</em>
        </span>
      </header>

      <main className="account-gate-page">
        <section className="account-gate-card">
          <div
            className="account-gate-progress"
            aria-hidden="true"
          >
            {[1, 2, 3].map((step) => (
              <span
                key={step}
                className={
                  step < currentStep
                    ? "is-done"
                    : step === currentStep
                      ? "is-active"
                      : ""
                }
              />
            ))}
          </div>

          <span className="account-gate-kicker">
            GoOurHub Account
          </span>

          {stage === "email" && (
            <>
              <div
                className="account-gate-icon"
                aria-hidden="true"
              >
                <FaEnvelopeOpenText />
              </div>

              <h1>
                {sessionHasKnownEmail &&
                savedSession?.firstName
                  ? `Hi ${capitalizeFirstName(
                      savedSession.firstName
                    )} 👋`
                  : "Welcome to GoOurHub 👋"}
              </h1>

              <p className="account-gate-description">
                {sessionHasKnownEmail
                  ? "We already know your signed-in email. Continue below to verify business access."
                  : "Enter your email to sign in or create your account."}
              </p>

              <form
                onSubmit={handleEmailSubmit}
                className="account-gate-form"
              >
                <label htmlFor="account-email">
                  {sessionHasKnownEmail
                    ? "Signed-in email"
                    : "Email address"}
                </label>

                <input
                  id="account-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="mike@example.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrorMessage("");
                  }}
                  readOnly={sessionHasKnownEmail}
                  aria-readonly={
                    sessionHasKnownEmail
                  }
                  disabled={isBusy}
                  autoFocus={!sessionHasKnownEmail}
                />

                <button
                  type="submit"
                  className="account-gate-button account-gate-button--primary"
                  disabled={isBusy}
                >
                  {isBusy ? (
                    <>
                      <span
                        className="account-gate-spinner"
                        aria-hidden="true"
                      />
                      Sending code…
                    </>
                  ) : (
                    <>
                      {sessionHasKnownEmail
                        ? "Continue"
                        : "Continue"}
                      <FaArrowRight aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>

              <p className="account-gate-note">
                {sessionHasKnownEmail
                  ? "Your email is already filled from your GoOurHub session."
                  : "No password needed. We will email you a 6-digit verification code."}
              </p>
            </>
          )}

          {stage === "code" && (
            <>
              <div
                className="account-gate-icon"
                aria-hidden="true"
              >
                <FaShieldAlt />
              </div>

              <h1>Check your email</h1>

              <p className="account-gate-description">
                We sent a 6-digit code to:
              </p>

              <p className="account-gate-email">
                {normalizedEmail}
              </p>

              <form
                onSubmit={handleCodeSubmit}
                className="account-gate-form"
              >
                <label htmlFor="account-code">
                  Verification code
                </label>

                <input
                  id="account-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(event) => {
                    const digitsOnly =
                      event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                    setVerificationCode(
                      digitsOnly
                    );
                    setErrorMessage("");
                  }}
                  disabled={isBusy}
                  autoFocus
                />

                <button
                  type="submit"
                  className="account-gate-button account-gate-button--primary"
                  disabled={
                    isBusy ||
                    verificationCode.length !== 6
                  }
                >
                  {isBusy ? (
                    <>
                      <span
                        className="account-gate-spinner"
                        aria-hidden="true"
                      />
                      Verifying…
                    </>
                  ) : (
                    <>
                      Verify and continue
                      <FaArrowRight aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>

              <button
                type="button"
                className="account-gate-button account-gate-button--text"
                onClick={requestVerificationCode}
                disabled={isBusy}
              >
                Resend code
              </button>

              {!sessionHasKnownEmail && (
                <button
                  type="button"
                  className="account-gate-button account-gate-button--text"
                  onClick={resetAccountGate}
                  disabled={isBusy}
                >
                  Use a different email
                </button>
              )}
            </>
          )}

          {stage === "verified" &&
            verifiedAccount && (
              <div className="account-gate-verified">
                <div
                  className="account-gate-success-icon"
                  aria-hidden="true"
                >
                  <FaCheck />
                </div>

                <h1>{greeting}</h1>

                <p className="account-gate-description">
                  {isReturningUser
                    ? `${verifiedAccount.email} matches an existing GoOurHub account.`
                    : `${verifiedAccount.email} is verified and ready for a new GoOurHub account.`}
                </p>

                {!isReturningUser && (
                  <p className="account-gate-note">
                    We will collect your name in the
                    next account step.
                  </p>
                )}

                <button
                  type="button"
                  className="account-gate-button account-gate-button--primary"
                  onClick={() =>
                    setStage("business")
                  }
                >
                  Continue to Add Biz
                  <FaArrowRight aria-hidden="true" />
                </button>

                {!sessionHasKnownEmail && (
                  <button
                    type="button"
                    className="account-gate-button account-gate-button--text"
                    onClick={resetAccountGate}
                  >
                    Use another email
                  </button>
                )}
              </div>
            )}

          {errorMessage &&
            stage !== "verified" && (
              <p
                className="account-gate-error"
                role="alert"
              >
                {errorMessage}
              </p>
            )}
        </section>
      </main>
    </div>
  );
}
